import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import lottoApi from "./api";
import { useAuth } from "../contexts/JWTContext";
import { getAttribution, pushToast } from "./lib";

const BANKS = [
  ["true", "ทรูมันนี่ วอลเล็ท"],
  ["004", "กสิกรไทย"],
  ["014", "ไทยพาณิชย์"],
  ["002", "กรุงเทพ"],
  ["006", "กรุงไทย"],
  ["011", "ทีทีบี (TTB)"],
  ["025", "กรุงศรีอยุธยา"],
  ["030", "ออมสิน"],
  ["034", "ธ.ก.ส."],
  ["033", "อาคารสงเคราะห์"],
  ["024", "ยูโอบี"],
  ["022", "ซีไอเอ็มบี"],
];

const DUP_MSG = {
  duplicateusername: "ชื่อผู้ใช้นี้มีอยู่แล้ว",
  duplicatebooknumber: "เลขบัญชีนี้ถูกใช้แล้ว",
  duplicatebookname: "ชื่อบัญชีนี้ถูกใช้แล้ว",
  duplicatebooknumberandusername: "ชื่อผู้ใช้และเลขบัญชีถูกใช้แล้ว",
};

// ฟอร์ม login/สมัครสมาชิก แบบใช้ซ้ำได้ — หน้า /auth ใช้แบบเต็ม, หน้าแรกฝังแบบย่อ (compact)
export default function AuthForm({ compact = false, onSuccess }) {
  const nav = useNavigate();
  const { login } = useAuth();
  // เข้ามาจากลิงก์แนะนำเพื่อน (?friend=) → เปิดแท็บสมัครให้เลย
  const [mode, setMode] = useState(() =>
    new URLSearchParams(window.location.search).get("friend") ? "register" : "login",
  );
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [f, setF] = useState({
    username: "",
    password: "",
    confirm: "",
    bank_name: "",
    book_number: "",
    book_name: "",
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const afterAuth = () => {
    if (onSuccess) onSuccess();
    else nav("/");
  };

  const doLogin = async () => {
    if (!f.username || !f.password) return pushToast("กรอกชื่อผู้ใช้และรหัสผ่าน", "err");
    setBusy(true);
    try {
      await login(f.username.trim(), f.password);
      pushToast("เข้าสู่ระบบสำเร็จ", "ok");
      afterAuth();
    } catch (e) {
      pushToast(e?.message || "เข้าสู่ระบบไม่สำเร็จ", "err");
    } finally {
      setBusy(false);
    }
  };

  const doRegister = async () => {
    if (!f.username || f.username.length < 4) return pushToast("ชื่อผู้ใช้อย่างน้อย 4 ตัว", "err");
    if (!f.password || f.password.length < 6) return pushToast("รหัสผ่านอย่างน้อย 6 ตัว", "err");
    if (f.password !== f.confirm) return pushToast("รหัสผ่านไม่ตรงกัน", "err");
    if (!f.bank_name) return pushToast("เลือกธนาคาร", "err");
    if (!f.book_number) return pushToast("กรอกเลขบัญชี", "err");
    if (!f.book_name) return pushToast("กรอกชื่อ-นามสกุลเจ้าของบัญชี", "err");

    setBusy(true);
    try {
      const friend = new URLSearchParams(window.location.search).get("friend") || 0;
      const chk = await lottoApi.checkRegister({
        username: f.username.trim(),
        book_number: f.book_number.trim(),
        bank_name: f.bank_name,
        book_name: f.book_name.trim(),
      });
      if (chk && chk !== "notfound") {
        pushToast(DUP_MSG[chk] || "ข้อมูลซ้ำในระบบ", "err");
        setBusy(false);
        return;
      }
      const attr = getAttribution();
      await lottoApi.register({
        username: f.username.trim(),
        password: f.password,
        book_name: f.book_name.trim(),
        book_number: f.book_number.trim(),
        bank_name: f.bank_name,
        inviterID: friend,
        registerFrom: attr.registerFrom || 0,
        clickId: attr.clickId || null,
        utm_source: attr.utm_source || null,
        utm_medium: attr.utm_medium || null,
        utm_campaign: attr.utm_campaign || null,
        utm_content: attr.utm_content || null,
        referrer: attr.referrer || null,
        landingPath: attr.landingPath || null,
        firstSeenAt: attr.firstSeenAt || null,
      });
      pushToast("สมัครสมาชิกสำเร็จ กำลังเข้าสู่ระบบ...", "ok");
      await login(f.username.trim(), f.password);
      afterAuth();
    } catch (e) {
      pushToast(e?.response?.data?.message || e?.message || "สมัครไม่สำเร็จ", "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={compact ? "lx-auth-card" : ""}>
      <div className="lx-authtabs">
        <button className={mode === "login" ? "on" : ""} onClick={() => setMode("login")}>
          เข้าสู่ระบบ
        </button>
        <button className={mode === "register" ? "on" : ""} onClick={() => setMode("register")}>
          สมัครสมาชิก
        </button>
      </div>

      <div className={compact ? "" : "lx-card"}>
        <div className="lx-field">
          <label>ชื่อผู้ใช้</label>
          <div className="lx-input-group">
            <span className="lx-input-ic">
              <Icon icon="solar:user-bold" />
            </span>
            <input className="lx-input" value={f.username} onChange={set("username")} autoCapitalize="none" />
          </div>
        </div>
        <div className="lx-field">
          <label>รหัสผ่าน</label>
          <div className="lx-input-group">
            <span className="lx-input-ic">
              <Icon icon="solar:lock-password-bold" />
            </span>
            <input
              className="lx-input"
              type={showPw ? "text" : "password"}
              value={f.password}
              onChange={set("password")}
            />
            <button
              type="button"
              className="lx-input-toggle"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
            >
              <Icon icon={showPw ? "solar:eye-closed-bold" : "solar:eye-bold"} />
            </button>
          </div>
        </div>

        {mode === "register" && (
          <>
            <div className="lx-field">
              <label>ยืนยันรหัสผ่าน</label>
              <div className="lx-input-group">
                <span className="lx-input-ic">
                  <Icon icon="solar:lock-password-bold" />
                </span>
                <input
                  className="lx-input"
                  type={showConfirmPw ? "text" : "password"}
                  value={f.confirm}
                  onChange={set("confirm")}
                />
                <button
                  type="button"
                  className="lx-input-toggle"
                  onClick={() => setShowConfirmPw((v) => !v)}
                  aria-label={showConfirmPw ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
                >
                  <Icon icon={showConfirmPw ? "solar:eye-closed-bold" : "solar:eye-bold"} />
                </button>
              </div>
            </div>
            <div className="lx-field">
              <label>ธนาคาร / ทรูวอลเล็ท</label>
              <select className="lx-input" value={f.bank_name} onChange={set("bank_name")}>
                <option value="">เลือกธนาคาร</option>
                {BANKS.map(([c, n]) => (
                  <option key={c} value={c}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="lx-field">
              <label>{f.bank_name === "true" ? "เบอร์ทรูวอลเล็ท" : "เลขบัญชี"}</label>
              <input
                className="lx-input"
                inputMode="numeric"
                value={f.book_number}
                onChange={(e) => setF((s) => ({ ...s, book_number: e.target.value.replace(/\D/g, "") }))}
              />
            </div>
            <div className="lx-field">
              <label>ชื่อ-นามสกุล เจ้าของบัญชี</label>
              <input className="lx-input" value={f.book_name} onChange={set("book_name")} />
            </div>
          </>
        )}

        <button
          className="lx-btn gradient block"
          style={{ marginTop: 6 }}
          disabled={busy}
          onClick={mode === "login" ? doLogin : doRegister}
        >
          <Icon icon={mode === "login" ? "solar:login-3-bold" : "solar:user-plus-bold"} width={18} />
          {busy ? "กำลังดำเนินการ..." : mode === "login" ? "เข้าสู่ระบบ" : "สมัครสมาชิก"}
        </button>

        {compact && (
          <div className="lx-auth-trust">
            <Icon icon="solar:shield-check-bold" width={14} />
            ปลอดภัย เข้ารหัสข้อมูลทุกขั้นตอน
          </div>
        )}
      </div>
    </div>
  );
}
